import React, { useEffect, useState } from 'react';
import CustomImage from '../Antd/Image';
import CustomSpin from '../Antd/Spin';

const PDF_VIEWER_STYLE = {
  width: '100%',
  height: '500px',
  border: '1px solid #d9d9d9',
  borderRadius: '2px',
};

const getPathname = (src) => {
  if (!src) return '';
  try {
    return new URL(src).pathname;
  } catch {
    return src.split('?')[0];
  }
};

const isPdfUrl = (src) => getPathname(src).toLowerCase().endsWith('.pdf');

const isImageUrl = (src) => /\.(jpe?g|png|gif|webp)$/i.test(getPathname(src));

const OpenInNewTabLink = ({ src }) => (
  <a href={src} target="_blank" rel="noopener noreferrer">
    Abrir en nueva pestaña
  </a>
);

const PdfViewer = ({ src, alt }) => {
  const [pdfSrc, setPdfSrc] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (!src) {
      setPdfSrc(null);
      setPdfLoading(false);
      return;
    }

    let objectUrl;
    let cancelled = false;

    setPdfSrc(null);
    setPdfLoading(true);

    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch PDF');
        return res.blob();
      })
      .then((blob) => {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setPdfSrc(url);
      })
      .catch(() => {
        // CORS or other fetch failure: fall back to remote URL (works for recipes / Safari)
        if (!cancelled) setPdfSrc(src);
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src) {
    return (
      <div
        style={{
          ...PDF_VIEWER_STYLE,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
        }}
      >
        <p>No se puede visualizar el archivo en el navegador.</p>
      </div>
    );
  }

  if (pdfLoading) {
    return (
      <div style={{ ...PDF_VIEWER_STYLE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CustomSpin />
      </div>
    );
  }

  const embedSrc = pdfSrc || src;

  return (
    <div>
      <div style={PDF_VIEWER_STYLE}>
        <embed
          src={embedSrc}
          type="application/pdf"
          style={{ width: '100%', height: '100%' }}
          title={alt || 'Visor de PDF'}
        />
      </div>
      <div style={{ textAlign: 'center', marginTop: '12px' }}>
        <OpenInNewTabLink src={src} />
      </div>
    </div>
  );
};

const CustomMediaViewer = ({ src, alt, onError, ...props }) => {
  const [viewAsPdf, setViewAsPdf] = useState(() => isPdfUrl(src));

  useEffect(() => {
    setViewAsPdf(isPdfUrl(src));
  }, [src]);

  if (viewAsPdf) {
    return <PdfViewer src={src} alt={alt} />;
  }

  if (isImageUrl(src)) {
    return <CustomImage src={src} alt={alt} onError={onError} {...props} />;
  }

  return (
    <CustomImage
      src={src}
      alt={alt}
      {...props}
      onError={(event) => {
        onError?.(event);
        setViewAsPdf(true);
      }}
    />
  );
};

export default CustomMediaViewer;
